import {
  LoaderCircle,
  Plus,
  Save,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  normalizarDecimal,
  normalizarMoneda,
  redondearMoneda,
} from '../../utils/numeros'
import {
  formatearKilosPos,
  formatearMontoPos,
} from '../../utils/pos'
import ModalMensaje from '../ui/ModalMensaje'
import ClienteRapidoPosModal from './ClienteRapidoPosModal'

const OPCIONES_TIPO_DESCUENTO = [
  { valor: 'ninguno', etiqueta: 'Sin descuento' },
  { valor: 'porcentaje', etiqueta: 'Porcentaje (%)' },
  { valor: 'monto', etiqueta: 'Monto ($)' },
]

// Decimales físicos (peso bruto, tara, cantidad): un punto es decimal.
function convertirNumero(valor) {
  const numero = normalizarDecimal(valor)

  return Number.isFinite(numero) ? numero : 0
}

// Montos monetarios (valor/kg, descuento en monto, Cajas/Rejillas): un
// punto es separador de miles, la coma es el decimal. Ver normalizarMoneda.
function convertirMoneda(valor) {
  const numero = normalizarMoneda(valor)

  return Number.isFinite(numero) ? numero : 0
}

function crearFilaVacia(id) {
  return {
    id,
    productoId: '',
    tipoEnvaseId: '',
    cantidad: '',
    pesoBruto: '',
    valorKilo: '',
  }
}

// Formulario de venta POS compartido entre Nueva venta y Editar venta:
// datos generales, productos, descuento y el total. La forma de pago (solo
// aplica a ventas nuevas, una venta editada nunca toca pagos) se inyecta
// como `children` para no acoplar este formulario a esa lógica.
function FormularioVentaPos({
  empresaId,
  valoresIniciales,
  bloqueado = false,
  deshabilitarGuardar = false,
  textoBoton = 'Guardar',
  iconoBoton: IconoBoton = Save,
  onCancelar,
  onValidSubmit,
  onCambiarTotales,
  children,
}) {
  const { modal, abrirModal } = useModalMensaje()

  const [fecha, setFecha] = useState('')
  const [clienteId, setClienteId] = useState('')
  const [observaciones, setObservaciones] = useState('')
  const [filas, setFilas] = useState([])
  const [estadoEnvases, setEstadoEnvases] = useState('pendiente')
  const [montoEnvasesPagado, setMontoEnvasesPagado] = useState('')
  const [tipoDescuento, setTipoDescuento] = useState('ninguno')
  const [valorDescuento, setValorDescuento] = useState('')

  const [clientes, setClientes] = useState([])
  const [productos, setProductos] = useState([])
  const [tiposEnvase, setTiposEnvase] = useState([])
  const [cargandoOpciones, setCargandoOpciones] = useState(true)
  const [clienteRapidoAbierto, setClienteRapidoAbierto] =
    useState(false)

  const contadorFilaRef = useRef(0)
  const empresaCargadaRef = useRef(null)

  const siguienteFilaId = () => {
    contadorFilaRef.current += 1
    return contadorFilaRef.current
  }

  useEffect(() => {
    if (!empresaId) return

    if (empresaCargadaRef.current === empresaId) return

    empresaCargadaRef.current = empresaId

    cargarOpciones()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId])

  useEffect(() => {
    if (!valoresIniciales) return

    contadorFilaRef.current = 0

    setFecha(valoresIniciales.fecha || '')
    setClienteId(valoresIniciales.clienteId || '')
    setObservaciones(valoresIniciales.observaciones || '')
    setEstadoEnvases(valoresIniciales.estadoEnvases || 'pendiente')
    setMontoEnvasesPagado(valoresIniciales.montoEnvasesPagado || '')
    setTipoDescuento(valoresIniciales.tipoDescuento || 'ninguno')
    setValorDescuento(valoresIniciales.valorDescuento || '')
    setFilas(
      (valoresIniciales.filas || []).map((fila) => ({
        id: siguienteFilaId(),
        productoId: fila.productoId || '',
        tipoEnvaseId: fila.tipoEnvaseId || '',
        cantidad: fila.cantidad ?? '',
        pesoBruto: fila.pesoBruto ?? '',
        valorKilo: fila.valorKilo ?? '',
      }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valoresIniciales])

  const cargarOpciones = async () => {
    if (!empresaId) return

    try {
      setCargandoOpciones(true)

      const [respuestaClientes, respuestaProductos, respuestaEnvases] =
        await Promise.all([
          supabase
            .from('clientes')
            .select('id, razon_social, rut')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('razon_social', { ascending: true }),
          supabase
            .from('productos')
            .select('id, codigo, nombre')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('nombre', { ascending: true }),
          supabase
            .from('tipos_envase')
            .select('id, nombre, tara_kg')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('nombre', { ascending: true }),
        ])

      if (respuestaClientes.error) throw respuestaClientes.error
      if (respuestaProductos.error) throw respuestaProductos.error
      if (respuestaEnvases.error) throw respuestaEnvases.error

      setClientes(respuestaClientes.data || [])
      setProductos(respuestaProductos.data || [])
      setTiposEnvase(respuestaEnvases.data || [])
    } catch (errorCarga) {
      console.error(
        'Error al cargar datos para la venta:',
        errorCarga
      )

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar datos',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar clientes, productos y tipos de envase.',
      })
    } finally {
      setCargandoOpciones(false)
    }
  }

  const alCrearClienteRapido = (clienteCreado) => {
    setClientes((actuales) =>
      [...actuales, clienteCreado].sort((a, b) =>
        a.razon_social.localeCompare(b.razon_social)
      )
    )
    setClienteId(clienteCreado.id)
    setClienteRapidoAbierto(false)

    abrirModal({
      tipo: 'success',
      titulo: 'Cliente creado',
      mensaje: 'Cliente creado correctamente.',
    })
  }

  const obtenerTaraUnitaria = (tipoEnvaseId) => {
    const tipo = tiposEnvase.find((item) => item.id === tipoEnvaseId)
    return Number(tipo?.tara_kg) || 0
  }

  const calcularTaraTotal = (fila) =>
    convertirNumero(fila.cantidad) * obtenerTaraUnitaria(fila.tipoEnvaseId)

  const calcularPesoNeto = (fila) =>
    convertirNumero(fila.pesoBruto) - calcularTaraTotal(fila)

  // Redondea cada línea a centavos ANTES de sumarla al subtotal, igual
  // que hace Supabase (`round((peso_bruto - tara_total) * valor_kilo, 2)`
  // por fila, sumado recién después). Sumar los productos sin redondear
  // por línea puede arrastrar centavos de diferencia frente al total que
  // calcula el backend — invisible en la UI (que redondea a pesos
  // enteros para mostrar) pero suficiente para que un pago con un monto
  // "limpio" quede, según el backend, por encima del total real.
  const calcularTotalLinea = (fila) =>
    redondearMoneda(calcularPesoNeto(fila) * convertirMoneda(fila.valorKilo))

  const agregarFila = () => {
    setFilas((actuales) => [
      ...actuales,
      crearFilaVacia(siguienteFilaId()),
    ])
  }

  const eliminarFila = (id) => {
    setFilas((actuales) => actuales.filter((fila) => fila.id !== id))
  }

  const combinacionYaAgregada = (idFila, productoId, tipoEnvaseId) => {
    if (!productoId || !tipoEnvaseId) return false

    return filas.some(
      (fila) =>
        fila.id !== idFila &&
        fila.productoId === productoId &&
        fila.tipoEnvaseId === tipoEnvaseId
    )
  }

  const cambiarProductoFila = (id, productoId) => {
    const filaActual = filas.find((fila) => fila.id === id)
    const tipoEnvaseId = filaActual?.tipoEnvaseId || ''

    if (combinacionYaAgregada(id, productoId, tipoEnvaseId)) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Combinación duplicada',
        mensaje:
          'Este producto con esta Caja/Rejilla ya fue agregado.',
      })
      return
    }

    setFilas((actuales) =>
      actuales.map((fila) =>
        fila.id === id ? { ...fila, productoId } : fila
      )
    )
  }

  const cambiarTipoEnvaseFila = (id, tipoEnvaseId) => {
    const filaActual = filas.find((fila) => fila.id === id)
    const productoId = filaActual?.productoId || ''

    if (combinacionYaAgregada(id, productoId, tipoEnvaseId)) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Combinación duplicada',
        mensaje:
          'Este producto con esta Caja/Rejilla ya fue agregado.',
      })
      return
    }

    setFilas((actuales) =>
      actuales.map((fila) =>
        fila.id === id ? { ...fila, tipoEnvaseId } : fila
      )
    )
  }

  const cambiarCampoFila = (id, campo, valor) => {
    setFilas((actuales) =>
      actuales.map((fila) =>
        fila.id === id ? { ...fila, [campo]: valor } : fila
      )
    )
  }

  const subtotalVenta = useMemo(() => {
    return filas.reduce(
      (acumulado, fila) => acumulado + calcularTotalLinea(fila),
      0
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filas, tiposEnvase])

  const totalKilos = useMemo(() => {
    return filas.reduce(
      (acumulado, fila) => acumulado + calcularPesoNeto(fila),
      0
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filas, tiposEnvase])

  // El % de descuento es un decimal físico (12,5% -> 12.5); el monto de
  // descuento es dinero (un punto es separador de miles, no decimal).
  const valorDescuentoNumero =
    tipoDescuento === 'monto'
      ? convertirMoneda(valorDescuento)
      : convertirNumero(valorDescuento)

  const montoDescuento = useMemo(() => {
    if (tipoDescuento === 'porcentaje') {
      return redondearMoneda(
        subtotalVenta * (valorDescuentoNumero / 100)
      )
    }

    if (tipoDescuento === 'monto') {
      return redondearMoneda(valorDescuentoNumero)
    }

    return 0
  }, [tipoDescuento, valorDescuentoNumero, subtotalVenta])

  const totalVenta = redondearMoneda(
    Math.max(subtotalVenta - montoDescuento, 0)
  )

  useEffect(() => {
    onCambiarTotales?.({
      subtotalVenta,
      montoDescuento,
      totalVenta,
      totalKilos,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotalVenta, montoDescuento, totalVenta, totalKilos])

  const cambiarTipoDescuento = (nuevoTipo) => {
    setTipoDescuento(nuevoTipo)
    setValorDescuento('')
  }

  // El monto pagado de Cajas/Rejillas es independiente del valor de los
  // productos: no se suma a subtotal/total de la venta. Si el usuario
  // vuelve a "pendiente" se limpia para no enviar un monto residual.
  const cambiarEstadoEnvases = (nuevoEstado) => {
    setEstadoEnvases(nuevoEstado)

    if (nuevoEstado === 'pendiente') {
      setMontoEnvasesPagado('')
    }
  }

  const montoEnvasesPagadoNumero = convertirMoneda(montoEnvasesPagado)

  const clienteSeleccionado = clientes.find(
    (cliente) => cliente.id === clienteId
  )

  const validarCampos = () => {
    if (!fecha) {
      abrirModal({
        tipo: 'error',
        titulo: 'Fecha requerida',
        mensaje: 'Debes ingresar la fecha de la venta.',
      })
      return false
    }

    if (filas.length === 0) {
      abrirModal({
        tipo: 'error',
        titulo: 'Productos requeridos',
        mensaje: 'Debes agregar al menos un producto a la venta.',
      })
      return false
    }

    const combinaciones = filas.map(
      (fila) => `${fila.productoId}|${fila.tipoEnvaseId}`
    )

    if (new Set(combinaciones).size !== combinaciones.length) {
      abrirModal({
        tipo: 'error',
        titulo: 'Combinación duplicada',
        mensaje: 'Este producto con esta Caja/Rejilla ya fue agregado.',
      })
      return false
    }

    for (const fila of filas) {
      if (!fila.productoId) {
        abrirModal({
          tipo: 'error',
          titulo: 'Producto requerido',
          mensaje: 'Debes seleccionar un producto en cada fila.',
        })
        return false
      }

      if (!fila.tipoEnvaseId) {
        abrirModal({
          tipo: 'error',
          titulo: 'Tipo de envase requerido',
          mensaje: 'Debes seleccionar un tipo de envase en cada fila.',
        })
        return false
      }

      const cantidadNumero = normalizarDecimal(fila.cantidad)

      if (!Number.isInteger(cantidadNumero) || cantidadNumero <= 0) {
        abrirModal({
          tipo: 'error',
          titulo: 'Cantidad inválida',
          mensaje:
            'La cantidad de Cajas/Rejillas debe ser un número entero mayor a cero.',
        })
        return false
      }

      if (convertirNumero(fila.pesoBruto) <= 0) {
        abrirModal({
          tipo: 'error',
          titulo: 'Peso bruto inválido',
          mensaje:
            'El peso bruto de cada producto debe ser mayor que cero.',
        })
        return false
      }

      if (convertirMoneda(fila.valorKilo) < 0) {
        abrirModal({
          tipo: 'error',
          titulo: 'Valor por kilo inválido',
          mensaje: 'El valor por kilo no puede ser negativo.',
        })
        return false
      }

      if (calcularPesoNeto(fila) <= 0) {
        abrirModal({
          tipo: 'error',
          titulo: 'Peso bruto insuficiente',
          mensaje:
            'El peso bruto debe ser mayor que la tara total de las cajas/rejillas.',
        })
        return false
      }
    }

    if (subtotalVenta <= 0) {
      abrirModal({
        tipo: 'error',
        titulo: 'Total inválido',
        mensaje: 'El total de la venta debe ser mayor que cero.',
      })
      return false
    }

    if (tipoDescuento === 'porcentaje') {
      const porcentaje = normalizarDecimal(valorDescuento)

      if (
        !Number.isFinite(porcentaje) ||
        porcentaje <= 0 ||
        porcentaje > 100
      ) {
        abrirModal({
          tipo: 'error',
          titulo: 'Porcentaje de descuento inválido',
          mensaje:
            'El porcentaje de descuento debe ser mayor a 0 y menor o igual a 100.',
        })
        return false
      }
    }

    if (tipoDescuento === 'monto') {
      const monto = normalizarMoneda(valorDescuento)

      if (
        !Number.isFinite(monto) ||
        monto <= 0 ||
        monto > subtotalVenta
      ) {
        abrirModal({
          tipo: 'error',
          titulo: 'Monto de descuento inválido',
          mensaje:
            'El descuento no puede superar el subtotal de la venta.',
        })
        return false
      }
    }

    if (estadoEnvases === 'pagado' && montoEnvasesPagadoNumero <= 0) {
      abrirModal({
        tipo: 'error',
        titulo: 'Monto pagado requerido',
        mensaje: 'Debe ingresar el monto pagado de Cajas/Rejillas.',
      })
      return false
    }

    return true
  }

  const manejarEnvio = (event) => {
    event.preventDefault()

    if (bloqueado) return
    if (!validarCampos()) return

    onValidSubmit?.({
      fecha,
      clienteId,
      clienteSeleccionado,
      observaciones,
      estadoEnvases,
      montoEnvasesPagado,
      montoEnvasesPagadoNumero:
        estadoEnvases === 'pagado' ? montoEnvasesPagadoNumero : 0,
      tipoDescuento,
      valorDescuento,
      valorDescuentoNumero,
      filas,
      subtotalVenta,
      montoDescuento,
      totalVenta,
      totalKilos,
      detallesPayload: filas.map((fila) => ({
        producto_id: fila.productoId,
        cantidad: convertirNumero(fila.cantidad),
        tipo_envase_id: fila.tipoEnvaseId,
        peso_bruto_kg: convertirNumero(fila.pesoBruto),
        valor_kilo: convertirMoneda(fila.valorKilo),
      })),
    })
  }

  if (cargandoOpciones || !valoresIniciales) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando formulario...
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <form
        onSubmit={manejarEnvio}
        className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <Campo label="Fecha" requerido>
            <input
              type="date"
              value={fecha}
              onChange={(event) => setFecha(event.target.value)}
              required
              disabled={bloqueado}
              className={estiloInput}
            />
          </Campo>

          <Campo label="Cliente">
            <select
              value={clienteId}
              onChange={(event) => setClienteId(event.target.value)}
              disabled={bloqueado}
              className={estiloInput}
            >
              <option value="">Venta sin cliente</option>

              {clientes.map((cliente) => (
                <option
                  key={cliente.id}
                  value={cliente.id}
                >
                  {cliente.razon_social}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setClienteRapidoAbierto(true)}
              disabled={bloqueado}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <UserPlus size={18} />
              Cliente rápido
            </button>
          </Campo>

          <div className="md:col-span-2 xl:col-span-1">
            <Campo label="Observaciones">
              <textarea
                value={observaciones}
                onChange={(event) =>
                  setObservaciones(event.target.value)
                }
                rows={1}
                placeholder="Información adicional de la venta"
                disabled={bloqueado}
                className={estiloInput}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Productos
            </h3>

            <button
              type="button"
              onClick={agregarFila}
              disabled={bloqueado}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Plus size={18} />
              Agregar producto
            </button>
          </div>

          {filas.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Aún no has agregado productos a la venta.
            </p>
          )}

          {filas.length > 0 && (
            <>
              {/* Mobile: una tarjeta apilada por producto (evita tabla ancha con scroll horizontal). */}
              <div className="space-y-4 md:hidden">
                {filas.map((fila) => {
                  const taraTotal = calcularTaraTotal(fila)
                  const pesoNeto = calcularPesoNeto(fila)
                  const totalLinea = calcularTotalLinea(fila)

                  return (
                    <div
                      key={fila.id}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Campo label="Producto">
                            <select
                              value={fila.productoId}
                              onChange={(event) =>
                                cambiarProductoFila(
                                  fila.id,
                                  event.target.value
                                )
                              }
                              disabled={bloqueado}
                              className={estiloInput}
                            >
                              <option value="">
                                Seleccionar producto
                              </option>

                              {productos.map((producto) => (
                                <option
                                  key={producto.id}
                                  value={producto.id}
                                >
                                  {producto.codigo} —{' '}
                                  {producto.nombre}
                                </option>
                              ))}
                            </select>
                          </Campo>
                        </div>

                        <button
                          type="button"
                          onClick={() => eliminarFila(fila.id)}
                          disabled={bloqueado}
                          title="Eliminar producto"
                          aria-label="Eliminar producto"
                          className="shrink-0 rounded-lg border border-red-200 p-3 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="mt-3">
                        <Campo label="Tipo de envase (Cajas/Rejillas)">
                          <select
                            value={fila.tipoEnvaseId}
                            onChange={(event) =>
                              cambiarTipoEnvaseFila(
                                fila.id,
                                event.target.value
                              )
                            }
                            disabled={bloqueado}
                            className={estiloInput}
                          >
                            <option value="">
                              Seleccionar tipo de envase
                            </option>

                            {tiposEnvase.map((tipo) => (
                              <option
                                key={tipo.id}
                                value={tipo.id}
                              >
                                {tipo.nombre} — Tara{' '}
                                {formatearKilosPos(tipo.tara_kg)}
                              </option>
                            ))}
                          </select>
                        </Campo>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <Campo label="Cantidad">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={fila.cantidad}
                            onChange={(event) =>
                              cambiarCampoFila(
                                fila.id,
                                'cantidad',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={bloqueado}
                            className={estiloInput}
                          />
                        </Campo>

                        <Campo label="Peso bruto">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={fila.pesoBruto}
                            onChange={(event) =>
                              cambiarCampoFila(
                                fila.id,
                                'pesoBruto',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={bloqueado}
                            className={estiloInput}
                          />
                        </Campo>

                        <Campo label="Tara total">
                          <p className="w-full truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-slate-700">
                            {formatearKilosPos(taraTotal)}
                          </p>
                        </Campo>

                        <Campo label="Peso neto">
                          <p className="w-full truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 font-semibold text-slate-900">
                            {formatearKilosPos(pesoNeto)}
                          </p>
                        </Campo>

                        <Campo label="Valor/kg">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={fila.valorKilo}
                            onChange={(event) =>
                              cambiarCampoFila(
                                fila.id,
                                'valorKilo',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={bloqueado}
                            className={estiloInput}
                          />
                        </Campo>

                        <Campo label="Total línea">
                          <p className="w-full truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 font-semibold text-slate-900">
                            {formatearMontoPos(totalLinea)}
                          </p>
                        </Campo>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Tablet / desktop: tabla completa. */}
              <div className="hidden w-full overflow-x-auto md:block">
                <table className="w-full min-w-[1080px] text-left text-sm">
                  <thead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-3">Producto</th>
                      <th className="py-2 pr-3">
                        Tipo de envase
                      </th>
                      <th className="py-2 pr-3">Cantidad</th>
                      <th className="py-2 pr-3">Peso bruto</th>
                      <th className="py-2 pr-3 text-right">
                        Tara total
                      </th>
                      <th className="py-2 pr-3 text-right">
                        Peso neto
                      </th>
                      <th className="py-2 pr-3">Valor/kg</th>
                      <th className="py-2 pr-3 text-right">
                        Total línea
                      </th>
                      <th className="py-2 pr-3 text-right">
                        Eliminar
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {filas.map((fila) => {
                      const taraTotal = calcularTaraTotal(fila)
                      const pesoNeto = calcularPesoNeto(fila)
                      const totalLinea = calcularTotalLinea(fila)

                      return (
                        <tr key={fila.id}>
                          <td className="py-3 pr-3">
                            <select
                              value={fila.productoId}
                              onChange={(event) =>
                                cambiarProductoFila(
                                  fila.id,
                                  event.target.value
                                )
                              }
                              disabled={bloqueado}
                              className={`${estiloInput} min-w-[200px]`}
                            >
                              <option value="">
                                Seleccionar producto
                              </option>

                              {productos.map((producto) => (
                                <option
                                  key={producto.id}
                                  value={producto.id}
                                >
                                  {producto.codigo} —{' '}
                                  {producto.nombre}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="py-3 pr-3">
                            <select
                              value={fila.tipoEnvaseId}
                              onChange={(event) =>
                                cambiarTipoEnvaseFila(
                                  fila.id,
                                  event.target.value
                                )
                              }
                              disabled={bloqueado}
                              className={`${estiloInput} min-w-[220px]`}
                            >
                              <option value="">
                                Seleccionar
                              </option>

                              {tiposEnvase.map((tipo) => (
                                <option
                                  key={tipo.id}
                                  value={tipo.id}
                                >
                                  {tipo.nombre} — Tara{' '}
                                  {formatearKilosPos(tipo.tara_kg)}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="py-3 pr-3">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={fila.cantidad}
                              onChange={(event) =>
                                cambiarCampoFila(
                                  fila.id,
                                  'cantidad',
                                  event.target.value
                                )
                              }
                              placeholder="0"
                              disabled={bloqueado}
                              className={`${estiloInput} min-w-[90px]`}
                            />
                          </td>

                          <td className="py-3 pr-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={fila.pesoBruto}
                              onChange={(event) =>
                                cambiarCampoFila(
                                  fila.id,
                                  'pesoBruto',
                                  event.target.value
                                )
                              }
                              placeholder="0"
                              disabled={bloqueado}
                              className={`${estiloInput} min-w-[110px]`}
                            />
                          </td>

                          <td className="whitespace-nowrap py-3 pr-3 text-right text-slate-600">
                            {formatearKilosPos(taraTotal)}
                          </td>

                          <td className="whitespace-nowrap py-3 pr-3 text-right font-semibold text-slate-900">
                            {formatearKilosPos(pesoNeto)}
                          </td>

                          <td className="py-3 pr-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={fila.valorKilo}
                              onChange={(event) =>
                                cambiarCampoFila(
                                  fila.id,
                                  'valorKilo',
                                  event.target.value
                                )
                              }
                              placeholder="0"
                              disabled={bloqueado}
                              className={`${estiloInput} min-w-[110px]`}
                            />
                          </td>

                          <td className="whitespace-nowrap py-3 pr-3 text-right font-semibold text-slate-900">
                            {formatearMontoPos(totalLinea)}
                          </td>

                          <td className="py-3 pr-3 text-right">
                            <button
                              type="button"
                              onClick={() => eliminarFila(fila.id)}
                              disabled={bloqueado}
                              title="Eliminar producto"
                              aria-label="Eliminar producto"
                              className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-slate-600">
                Subtotal
              </span>
              <span className="font-bold text-slate-900">
                {formatearMontoPos(subtotalVenta)}
              </span>
            </div>

            <p className="mt-1 text-right text-xs text-slate-500">
              {formatearKilosPos(totalKilos)}
            </p>

            <div className="mt-5 border-t border-slate-200 pt-5">
              <Campo label="Descuento">
                <select
                  value={tipoDescuento}
                  onChange={(event) =>
                    cambiarTipoDescuento(event.target.value)
                  }
                  disabled={bloqueado}
                  className={estiloInput}
                >
                  {OPCIONES_TIPO_DESCUENTO.map((opcion) => (
                    <option
                      key={opcion.valor}
                      value={opcion.valor}
                    >
                      {opcion.etiqueta}
                    </option>
                  ))}
                </select>
              </Campo>

              {tipoDescuento !== 'ninguno' && (
                <div className="mt-4">
                  <Campo
                    label={
                      tipoDescuento === 'porcentaje'
                        ? 'Porcentaje descuento'
                        : 'Monto descuento'
                    }
                    requerido
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      value={valorDescuento}
                      onChange={(event) =>
                        setValorDescuento(event.target.value)
                      }
                      placeholder="0"
                      disabled={bloqueado}
                      className={estiloInput}
                    />
                  </Campo>

                  <p className="mt-2 text-sm font-semibold text-slate-700">
                    Descuento aplicado: -{' '}
                    {formatearMontoPos(montoDescuento)}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col items-center rounded-xl bg-emerald-50 p-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                Total venta
              </p>

              <p className="mt-1 text-3xl font-bold text-emerald-900">
                {formatearMontoPos(totalVenta)}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Cajas/Rejillas
          </h3>

          <ToggleEstadoEnvases
            valor={estadoEnvases}
            onCambiar={cambiarEstadoEnvases}
            disabled={bloqueado}
          />

          {estadoEnvases === 'pagado' && (
            <div className="mt-4 max-w-xs">
              <Campo label="Monto pagado Cajas/Rejillas" requerido>
                <input
                  type="text"
                  inputMode="decimal"
                  value={montoEnvasesPagado}
                  onChange={(event) =>
                    setMontoEnvasesPagado(event.target.value)
                  }
                  placeholder="0"
                  disabled={bloqueado}
                  className={estiloInput}
                />
              </Campo>
            </div>
          )}
        </div>

        {children}

        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={bloqueado}
            className="w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={bloqueado || deshabilitarGuardar}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
          >
            <IconoBoton size={20} />
            {textoBoton}
          </button>
        </div>
      </form>

      {clienteRapidoAbierto && (
        <ClienteRapidoPosModal
          onClose={() => setClienteRapidoAbierto(false)}
          onCreado={alCrearClienteRapido}
        />
      )}

      <ModalMensaje {...modal} />
    </>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

function ToggleEstadoEnvases({ valor, onCambiar, disabled }) {
  return (
    <div className="flex w-full overflow-hidden rounded-lg border border-slate-300 sm:inline-flex sm:w-auto">
      <button
        type="button"
        onClick={() => onCambiar('pendiente')}
        disabled={disabled}
        className={`flex-1 px-6 py-3 text-sm font-semibold transition disabled:cursor-not-allowed sm:flex-none ${
          valor === 'pendiente'
            ? 'bg-slate-800 text-white'
            : 'bg-white text-slate-600 hover:bg-slate-100'
        }`}
      >
        Pendiente
      </button>

      <button
        type="button"
        onClick={() => onCambiar('pagado')}
        disabled={disabled}
        className={`flex-1 border-l border-slate-300 px-6 py-3 text-sm font-semibold transition disabled:cursor-not-allowed sm:flex-none ${
          valor === 'pagado'
            ? 'bg-emerald-600 text-white'
            : 'bg-white text-slate-600 hover:bg-slate-100'
        }`}
      >
        Pagado
      </button>
    </div>
  )
}

export default FormularioVentaPos
